import React, { useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { type ColumnDef } from '@tanstack/react-table'
import DataTable from '../../components/DataTable'
import Button from '../components/Button'
import { useSchoolStore } from '../../stores/schoolsStore'
import { School } from '../../types'
import Icon from '../components/Icon'

const Schools: React.FC = () => {
  const navigate = useNavigate()
  const { schools, loading, error, fetchSchools, removeSchool } =
    useSchoolStore()

  // Fetch schools on component mount
  useEffect(() => {
    fetchSchools()
  }, [fetchSchools])

  const handleDeleteSchool = async (id: string, displayName: string) => {
    if (
      window.confirm(
        `Are you sure you want to delete "${displayName}"? This action cannot be undone.`,
      )
    ) {
      try {
        await removeSchool(id)
      } catch (err) {
        console.error('Error deleting school:', err)
      }
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  // Define columns for DataTable
  const columns = useMemo<ColumnDef<School>[]>(
    () => [
      {
        accessorKey: 'displayName',
        header: 'Display Name',
        cell: (info) => (
          <div className="text-sm font-medium text-gray-900">
            {info.getValue() as string}
          </div>
        ),
      },
      {
        accessorKey: 'importNames',
        header: 'Import Names',
        cell: (info) => {
          const importNames = info.getValue() as string[]
          return (
            <div className="text-sm text-gray-600">
              {importNames?.length > 0
                ? `${importNames.length} aliases`
                : 'None'}
            </div>
          )
        },
      },
      {
        accessorKey: 'createdAt',
        header: 'Created',
        cell: (info) => (
          <div className="text-sm text-gray-500">
            {formatDate(info.getValue() as string)}
          </div>
        ),
      },
      {
        accessorKey: 'updatedAt',
        header: 'Last Updated',
        cell: (info) => (
          <div className="text-sm text-gray-500">
            {formatDate(info.getValue() as string)}
          </div>
        ),
      },
      {
        id: 'actions',
        header: 'Actions',
        cell: ({ row }) => (
          <div className="text-sm font-medium">
            <Button
              onClick={(e) => {
                e.stopPropagation()
                navigate(`/manage/schools/${row.original._id}`)
              }}
              variant="link"
              className="mr-3"
            >
              Edit
            </Button>
            <Button
              onClick={(e) => {
                e.stopPropagation()
                handleDeleteSchool(row.original._id, row.original.displayName)
              }}
              variant="link"
              className="text-red-600 hover:text-red-900"
            >
              Delete
            </Button>
          </div>
        ),
      },
    ],
    [],
  )

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Schools</h1>
          <p className="text-gray-600">Manage schools in your organizations</p>
        </div>
        <Button
          onClick={() => navigate('/manage/schools/new')}
          variant="primary"
          className="ml-auto flex items-center gap-2"
        >
          <Icon type="plus" />
          Create School
        </Button>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Schools Table */}
      <DataTable
        data={schools}
        columns={columns}
        searchPlaceholder="Search schools..."
        emptyStateTitle="No schools"
        emptyStateDescription="Get started by creating your first school."
        emptyStateIcon={
          <Icon type="building" className="mx-auto h-12 w-12 text-gray-400" />
        }
        onCreateClick={() => navigate('/manage/schools/new')}
        createButtonText="Create School"
        loading={loading}
        onRowClick={(schoolItem) =>
          navigate(`/manage/schools/${schoolItem._id}`)
        }
      />
    </div>
  )
}

export default Schools
